import { useSelector } from '@store';
import { useSnackbar } from 'notistack';
import AppConfig from '@config';
import { dateCalculator, formatDate } from '@utils/formatter';
import { RequestPayResponse } from '../../@types/@iamport-type';
import {
  PgEnum,
  RequestPaymentInput,
  RequestPaymentResultCode,
  PayMethodEnum
} from '../types/payment';
import { CertificationResponse } from '../../@types/@iamport-type/Certification/CertificationCallback';

type Days = number;
const V_BANK_DEADLINE_DAYS: Days = 1;

const isDevEnv = ['development', 'staging'].includes(AppConfig.mode);

const useIamport = () => {
  const { IMP } = window;
  const { enqueueSnackbar } = useSnackbar();
  const env = AppConfig.mode;
  const buyer_name = useSelector((store) => store.client?.managerName);
  const buyer_email = useSelector((store) => store.client.email);
  const buyer_tel = useSelector((store) => store.client.cellphoneNumber ?? '');

  const getVirtualAccountExpiredDate = (pay_method: PayMethodEnum) => {
    if (pay_method !== PayMethodEnum.vbank) return;

    return formatDate(
      (dateCalculator(new Date(), V_BANK_DEADLINE_DAYS) as Date).toISOString(),
      'YYYYMMDDHHmm'
    );
  };

  /**
   * A function that requests payment.
   ** 필수값 외 데이터는 고객 데이터 기본값이 적용됨.
   * @param {RequestPaymentInput} input - RequestPaymentInput
   */
  /* For the merchant_uid. */
  const requestPayment = async (input: RequestPaymentInput) => {
    if (!IMP) {
      enqueueSnackbar(RequestPaymentResultCode.FAIL_INITIALIZE_IAMPORT);
      return;
    }

    if (!buyer_name) {
      enqueueSnackbar(RequestPaymentResultCode.MISSING_REQUIRED_INFO);
      return;
    }

    const mobileRedirectUrl = () => {
      const { host, pathname } = window.location;
      const menuParam = 'menu=create_purchaseorder';
      return host + pathname + '?' + menuParam;
    };

    return new Promise<RequestPayResponse>((res, rej) => {
      IMP.request_pay(
        {
          buyer_name,
          pg: isDevEnv ? `${PgEnum.nice}.nictest00m` : PgEnum.nice,
          m_redirect_url: mobileRedirectUrl(),
          escrow: true,
          buyer_tel,
          vbank_due: getVirtualAccountExpiredDate(input.pay_method),
          buyer_email,
          notice_url: `${AppConfig.IMPWebhookUrl}?env=${env}`,
          ...input
        },
        (result) => {
          if (result.success) {
            res(result);
          } else {
            rej(new Error(result.error_msg));
          }
        }
      );
    });
  };

  const certificate = async () => {
    return new Promise<CertificationResponse>((resolve, reject) => {
      IMP?.certification(
        {
          company: `${AppConfig.baseUrl}`,
          popup: true,
          m_redirect_url: `${AppConfig.baseUrl}/mypage/profile`
        },
        (response) => {
          resolve(response);
        }
      );
    });
  };

  return {
    requestPayment,
    certificate
  };
};

export default useIamport;
